import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../api'
import Fox from './Fox'
import { faqPourCasquette, rinaCherche, rinaSuggestions, RINA_SALUTS, normaliser } from '../content/faq'

/**
 * RINA — PAGE CONVERSATIONNELLE plein ecran (prototype v7.4).
 *
 * Decision Enzo actee dans la v7.4 : en PWA, l'enregistrement audio n'est pas
 * fiable ; la premiere impression de RINA ne peut pas en dependre. On passe donc
 * a une conversation ECRITE plein ecran. Le micro n'est pas supprime — il vit
 * derriere `RINA_AUDIO`, a repasser a `true` dans l'app native. Un seul ecran a
 * maintenir.
 *
 * D'OU VIENNENT LES REPONSES, dans cet ordre :
 *  1. **La FAQ** (`content/faq.js`) — elle fait autorite sur les regles et les
 *     chiffres (commission, sequestre, certifications). La reponse porte alors
 *     sa source en clair.
 *  2. **Le backend `/rina/chat`** — intentions sur les DONNEES VIVES (prochains
 *     evenements, produits, producteurs), avec ses cartes. Le backend renvoie
 *     son `intent` : `UNKNOWN` signifie qu'il n'a pas compris, et on n'affiche
 *     pas sa reponse d'excuse.
 *  3. **Sinon RINA le DIT** et passe la main a l'equipe. Elle n'improvise
 *     jamais : sur une commission ou une certification, une reponse
 *     approximative vaut moins que rien.
 *
 * Perimetre affiche sous la saisie : RINA repond et oriente, elle n'agit pas.
 */

const RINA_AUDIO = false // PWA : pas de micro. Passer a true dans l'app native.
const CREME = 'rgb(var(--c-creme))'

const nouvelId = () => `m${Date.now()}${Math.floor(Math.random() * 1000)}`

/** Libelle du lieu d'ouverture, pour la pastille de contexte. */
function contexteDe(lieu, hubMode) {
  const LIEUX = {
    marche: { e: '🎪', l: 'Évènements' },
    paniers: { e: '🧺', l: 'Paniers' },
    wallet: { e: '👛', l: 'Mon wallet' },
    facturation: { e: '🧾', l: 'Ma facturation' },
    profilprod: { e: '🧑🏾‍🌾', l: 'Mon profil producteur' },
    offers: { e: '📦', l: 'Mes offres' },
    favorites: { e: '⭐', l: 'Mes favoris' },
    account: { e: '👤', l: 'Mon compte' },
    explore: { e: '🧭', l: "l'Explorer" },
  }
  const base = LIEUX[lieu] || { e: '🏠', l: "l'accueil" }
  return { ...base, casquette: hubMode === 'producer' ? 'producteur' : 'client' }
}

function RinaChatModal({
  onClose, onNavigate, isAuthenticated, user, hubMode = 'client', lieu = null,
  onCta, onExplore,
}) {
  const cats = faqPourCasquette(hubMode)
  const ctx = contexteDe(lieu, hubMode)
  const basRef = useRef(null)

  const [fil, setFil] = useState(() => [{
    id: nouvelId(), de: 'rina',
    txt: `Bonjou ! Mwen sé RINA. Vous êtes sur ${ctx.l} — dites-moi ce qu'il vous faut, je cherche la réponse et je vous emmène au bon endroit.`,
  }])
  const [saisie, setSaisie] = useState('')
  const [ecrit, setEcrit] = useState(false)

  const suggestions = rinaSuggestions(lieu, hubMode)
  const vierge = fil.length <= 1 && !ecrit

  useEffect(() => {
    basRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' })
  }, [fil.length, ecrit])

  /** Reponse hors ligne / dernier recours — jamais une invention. */
  const aveuDIgnorance = () => (isAuthenticated
    ? {
      txt: "Là, je n'ai pas la réponse — et je préfère vous le dire plutôt que d'inventer. Sur une commission, une certification ou une commande, une réponse approximative vaut moins que rien. Je passe la main à l'équipe ?",
      cta: { l: "Écrire à l'équipe", vue: 'aideContact' },
    }
    : {
      txt: "Là, je n'ai pas la réponse — et je préfère vous le dire plutôt que d'inventer. Écrivez à l'équipe sur contact@manrina.fr, ou venez nous voir au marché samedi : on répond toujours.",
    })

  const repondre = async (q) => {
    // 1. La FAQ d'abord : elle fait autorite sur les regles et les chiffres.
    const trouve = rinaCherche(q, cats)
    if (trouve) return { txt: trouve.it.r, cta: trouve.it.cta, source: trouve.cat.titre }

    // 2. Le backend pour les donnees vives (evenements, produits, producteurs).
    try {
      const r = await api.rina.chat([{ role: 'user', content: q }], { hubMode })
      if (r && r.intent && r.intent !== 'UNKNOWN' && r.message) {
        return { txt: r.message, cards: r.cards, suggestions: r.suggestions }
      }
    } catch {
      // Hors ligne : on retombe sur les politesses locales puis sur l'aveu.
    }

    // 3. Politesses reconnues, sinon aveu d'ignorance + passage a l'humain.
    const n = normaliser(q)
    const salut = RINA_SALUTS.find((s) => s.mots.some((m) => n.includes(m)))
    if (salut) return { txt: salut.r }
    return aveuDIgnorance()
  }

  const envoyer = async (texte) => {
    const q = String(texte || '').trim()
    if (!q || ecrit) return
    setFil((f) => [...f, { id: nouvelId(), de: 'moi', txt: q }])
    setSaisie('')
    setEcrit(true)
    const r = await repondre(q)
    setEcrit(false)
    setFil((f) => [...f, { id: nouvelId(), de: 'rina', ...r }])
  }

  const recommencer = () => {
    setEcrit(false)
    setFil([{ id: nouvelId(), de: 'rina', txt: `On repart de zéro. Vous êtes sur ${ctx.l} — qu'est-ce qu'il vous faut ?` }])
  }

  const suivreCta = (cta) => { onClose(); onCta?.(cta) }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col animate-sheet-up"
      style={{ background: 'linear-gradient(165deg, #052e2d 0%, #041918 55%, #06302c 100%)' }}>

      {/* En-tete : identite + contexte d'ouverture */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 pt-3.5 pb-3"
        style={{ borderBottom: '1px solid rgba(247,240,234,0.1)', paddingTop: 'max(0.875rem, env(safe-area-inset-top))' }}>
        <span className="w-11 h-11 rounded-[15px] flex-shrink-0 flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,rgba(244,137,83,0.42),rgba(247,240,234,0.12))', border: '1px solid rgba(247,240,234,0.28)' }}>
          <Fox size={30} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-[21px] leading-none" style={{ color: CREME }}>RINA</p>
          <span className="inline-flex items-center gap-1.5 mt-1 pl-2 pr-2.5 py-[3px] rounded-full text-[10.5px] font-bold"
            style={{ background: 'rgba(20,184,166,0.18)', border: '1px solid rgba(20,184,166,0.32)', color: '#7ee8d8' }}>
            {ctx.e} depuis {ctx.l}
          </span>
        </div>
        {fil.length > 1 && (
          <button onClick={recommencer} aria-label="Recommencer la conversation"
            className="w-9 h-9 rounded-full flex-shrink-0 text-[15px]"
            style={{ border: '1px solid rgba(247,240,234,0.2)', background: 'rgba(247,240,234,0.08)', color: 'rgba(247,240,234,0.7)' }}>↺</button>
        )}
        <button onClick={onClose} aria-label="Fermer"
          className="w-9 h-9 rounded-full flex-shrink-0 text-[17px] font-bold"
          style={{ border: '1px solid rgba(247,240,234,0.2)', background: 'rgba(247,240,234,0.08)', color: CREME }}>×</button>
      </div>

      {/* Fil de conversation */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2">
        {fil.map((m) => m.de === 'moi' ? (
          <div key={m.id} className="flex justify-end mb-3">
            <p className="max-w-[82%] px-3.5 py-2.5 text-[13.5px] font-semibold leading-relaxed text-white"
              style={{ borderRadius: '20px 20px 6px 20px', background: 'linear-gradient(135deg, rgb(var(--c-primary)), #e0653a)' }}>
              {m.txt}
            </p>
          </div>
        ) : (
          <div key={m.id} className="flex gap-2.5 mb-3" style={{ animation: 'bubbleIn .35s ease both' }}>
            <span className="w-[30px] h-[30px] rounded-[11px] flex-shrink-0 flex items-center justify-center mt-0.5"
              style={{ background: 'rgba(244,137,83,0.22)', border: '1px solid rgba(247,240,234,0.18)' }}>
              <Fox size={20} />
            </span>
            <div className="max-w-[84%]">
              <div className="px-3.5 py-2.5"
                style={{ borderRadius: '20px 20px 20px 6px', background: 'rgba(247,240,234,0.09)', border: '1px solid rgba(247,240,234,0.14)' }}>
                <p className="text-[13.5px] leading-[1.58]" style={{ color: 'rgba(247,240,234,0.92)' }}>{m.txt}</p>
                {m.source && (
                  <p className="text-[10px] font-bold tracking-wide mt-2" style={{ color: 'rgba(247,240,234,0.4)' }}>
                    Source · {m.source}
                  </p>
                )}
              </div>

              {/* Cartes du backend (evenements, produits, producteurs) */}
              {Array.isArray(m.cards) && m.cards.length > 0 && (
                <div className="flex flex-col gap-2 mt-2">
                  {m.cards.slice(0, 3).map((c, i) => (
                    <button key={i}
                      onClick={() => { if (c.type === 'marche') { onClose(); onExplore?.(c.data) } }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left rounded-2xl"
                      style={{ background: 'rgba(247,240,234,0.07)', border: '1px solid rgba(247,240,234,0.16)' }}>
                      <span className="text-lg flex-shrink-0">{c.type === 'marche' ? '🎪' : c.type === 'producteur' ? '🧑🏾‍🌾' : '🥭'}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[12.5px] font-bold truncate" style={{ color: CREME }}>
                          {c.data?.nom || c.data?.lieu || 'Voir'}
                        </span>
                        {c.data?.lieu && c.data?.nom && (
                          <span className="block text-[11px] truncate" style={{ color: 'rgba(247,240,234,0.55)' }}>📍 {c.data.lieu}</span>
                        )}
                      </span>
                      <span className="text-[13px]" style={{ color: 'rgba(247,240,234,0.4)' }}>›</span>
                    </button>
                  ))}
                </div>
              )}

              {m.cta && (
                <button onClick={() => suivreCta(m.cta)}
                  className="mt-2 px-4 py-2.5 rounded-full text-xs font-extrabold"
                  style={{ border: '1px solid rgba(244,137,83,0.5)', background: 'rgba(244,137,83,0.16)', color: '#ffc9a3' }}>
                  {m.cta.l} ›
                </button>
              )}
            </div>
          </div>
        ))}

        {ecrit && (
          <div className="flex gap-2.5 mb-3">
            <span className="w-[30px] h-[30px] rounded-[11px] flex-shrink-0 flex items-center justify-center mt-0.5"
              style={{ background: 'rgba(244,137,83,0.22)', border: '1px solid rgba(247,240,234,0.18)' }}>
              <Fox size={20} />
            </span>
            <div className="px-4 py-3 flex gap-1.5 items-center"
              style={{ borderRadius: '20px 20px 20px 6px', background: 'rgba(247,240,234,0.09)', border: '1px solid rgba(247,240,234,0.14)' }}>
              {[0, 1, 2].map((i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full"
                  style={{ background: 'rgba(247,240,234,0.55)', animation: `pulseDot 1s ${i * 0.15}s infinite ease-in-out` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={basRef} />
      </div>

      {/* Suggestions — tant que la conversation n'a pas demarre */}
      {vierge && (
        <div className="flex-shrink-0 px-4 pb-2.5">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] mx-0.5 mb-2"
            style={{ color: 'rgba(247,240,234,0.42)' }}>Souvent demandé</p>
          <div className="flex flex-col gap-[7px]">
            {suggestions.map((it) => (
              <button key={it.id} onClick={() => envoyer(it.q)}
                className="w-full text-left px-3.5 py-2.5 rounded-2xl text-[12.5px] font-semibold"
                style={{ border: '1px solid rgba(247,240,234,0.18)', background: 'rgba(247,240,234,0.06)', color: 'rgba(247,240,234,0.88)' }}>
                {it.q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Saisie — le micro n'apparait que dans l'app native (RINA_AUDIO) */}
      <div className="flex-shrink-0 px-3.5 pt-2.5"
        style={{ borderTop: '1px solid rgba(247,240,234,0.1)', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        <div className="flex items-end gap-2">
          {RINA_AUDIO && (
            <button className="w-11 h-11 rounded-full flex-shrink-0 text-lg"
              style={{ border: '1px solid rgba(247,240,234,0.2)', background: 'rgba(247,240,234,0.08)' }}>🎙️</button>
          )}
          <textarea
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envoyer(saisie) } }}
            rows={1}
            placeholder="Écrivez votre question…"
            aria-label="Votre question"
            className="flex-1 min-h-[44px] max-h-[120px] rounded-[22px] px-4 py-3 text-[13.5px] leading-snug resize-none outline-none"
            style={{ border: '1px solid rgba(247,240,234,0.2)', background: 'rgba(247,240,234,0.08)', color: CREME }}
          />
          <button onClick={() => envoyer(saisie)} disabled={!saisie.trim() || ecrit}
            aria-label="Envoyer"
            className="w-11 h-11 rounded-full flex-shrink-0 text-[17px] font-extrabold"
            style={saisie.trim() && !ecrit
              ? { background: 'linear-gradient(135deg, rgb(var(--c-primary)), #e0653a)', color: '#fff' }
              : { background: 'rgba(247,240,234,0.12)', color: 'rgba(247,240,234,0.35)' }}>↑</button>
        </div>
        <p className="text-[10px] text-center mt-2 leading-snug" style={{ color: 'rgba(247,240,234,0.34)' }}>
          RINA répond et vous oriente — elle ne commande rien et ne modifie pas vos stocks.
        </p>
      </div>
    </div>,
    document.body,
  )
}

export default RinaChatModal
